import React, { useState, useEffect } from 'react';
import AdminContainer from '../components/layout/AdminContainer';
import { fetchAdminUsage } from '../services/api';
import { Activity, Clock } from 'lucide-react';
import './AdminDashboardPage.css';

export default function AdminUsagePage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data: res } = await fetchAdminUsage();
                setData(res);
            } catch (err) {
                console.error('Failed to load usage:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const formatTime = (isoStr) => {
        if (!isoStr) return '—';
        try {
            const d = new Date(isoStr);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        } catch { return '—'; }
    };

    return (
        <AdminContainer>
            <div className="admin-page">
                <div className="admin-page-header">
                    <div>
                        <h1><Activity size={22} /> Usage & Costs</h1>
                        <p>Per-client messaging usage and platform audit trail</p>
                    </div>
                </div>

                {loading ? (
                    <div className="admin-loading">Loading usage data...</div>
                ) : (
                    <>
                        {/* Per-client messaging table */}
                        <div className="admin-card" style={{ marginBottom: 24 }}>
                            <div className="admin-card__header">
                                <span className="admin-card__title">Messaging Usage</span>
                            </div>
                            <div className="admin-table-wrap">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th>Sent</th>
                                            <th>Received</th>
                                            <th>Total</th>
                                            <th>WhatsApp</th>
                                            <th>Last Active</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data?.clients || []).map(c => (
                                            <tr key={c.id}>
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                                                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{c.username}</div>
                                                </td>
                                                <td>{c.messages_sent}</td>
                                                <td>{c.messages_received}</td>
                                                <td style={{ fontWeight: 600 }}>{c.messages_sent + c.messages_received}</td>
                                                <td>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', color: '#1a1b2e', fontWeight: 500 }}>
                                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.whatsapp_connected ? '#22c55e' : '#94a3b8', display: 'inline-block', flexShrink: 0 }} />
                                                        {c.whatsapp_connected ? 'Connected' : 'Not linked'}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                                    {formatTime(c.last_active)}
                                                </td>
                                            </tr>
                                        ))}
                                        {(!data?.clients || data.clients.length === 0) && (
                                            <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No clients yet</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* AI Token costs — Phase 6 */}
                        <div className="admin-card" style={{ marginBottom: 24 }}>
                            <div className="admin-card__header">
                                <span className="admin-card__title">AI Token Usage & Costs</span>
                            </div>
                            <div className="admin-loading" style={{ padding: 32 }}>
                                Coming in Phase 6 — AI cost tracking per client
                            </div>
                        </div>

                        {/* Audit Trail */}
                        <div className="admin-card">
                            <div className="admin-card__header">
                                <span className="admin-card__title">
                                    <Clock size={16} /> Live Audit Trail
                                </span>
                            </div>
                            {(data?.audit_trail?.length > 0) ? (
                                <ul className="audit-list">
                                    {data.audit_trail.map((entry, i) => (
                                        <li className="audit-item" key={i}>
                                            <span className="audit-item__time">{formatTime(entry.ts)}</span>
                                            <span className="audit-item__client">{entry.client_name || '—'}</span>
                                            <span className="audit-item__detail">
                                                <strong>{entry.action}</strong> {entry.detail}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="admin-loading" style={{ padding: 32 }}>
                                    No audit entries yet
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </AdminContainer>
    );
}
